// @flow
import addPageAnnotations from 'app/packages/trace-logger/utils/addPageAnnotations';
import addPageBinaryAnnotations from 'app/packages/trace-logger/utils/addPageBinaryAnnotations';
import addPageSubStopwatches from 'app/packages/trace-logger/utils/addPageSubStopwatches';
import addStopwatchToDebugPerfLogger from 'app/packages/trace-logger/utils/addStopwatchToDebugPerfLogger';
import ajax from 'app/common/lib/Ajax';
import context from 'app/common/lib/context';
import experiments from 'app/common/lib/experiments';
import randomTraceId from 'app/packages/trace-logger/utils/generateTraceId';
import Stopwatch from 'app/packages/trace-logger/Stopwatch';
import TraceEventHandler from 'app/packages/trace-logger/TraceEventHandler';
import { getAppInitAnnotations } from 'app/common/lib/TraceLogger/appInitAnnotations';
import type {
  DesktopTraceEventHandlerOptions,
  TraceEvent,
  CustomTraceEvent,
} from 'app/common/lib/TraceLogger/types';
import { DEBUG_ENABLED, DEBUG_TRACING } from 'app/packages/trace-logger/debugFlags';
import { inNewTraceLoggerExperiment, getGridHandler } from 'app/common/lib/TraceLogger/gridHandler';

export default class DesktopTraceEventHandler extends TraceEventHandler {
  constructor({
    metricName,
    traceName,
    triggeredBy,
    annotations,
    binaryAnnotations,
    viewType,
    viewParameter,
    endTimeConstraints,
    sampleRate,
    experimentNames,
    customEventHooks,
    measureMasonry,
  }: DesktopTraceEventHandlerOptions) {
    const desktopContext = context.instance;
    const {
      analysis_ua: { browser_name: browserName, browser_version: browserVersion, os_name: osName },
      app_version: appVersion,
      is_authenticated: isAuthenticated,
      is_bot: isBotString,
      locale,
    } = desktopContext;
    super(metricName, ajax, {
      appVersion,
      browserName,
      browserVersion,
      isAuthenticated,
      isBot: isBotString === 'true',
      isMobile: false,
      locale,
      osName,
      stageName: context.initialPageContext.stage || '',
      userId: desktopContext.is_authenticated ? desktopContext.user.id : undefined,
    });
    this.traceName = traceName;
    this.traceLoggerVersion = 'desktop2';

    this.viewType = viewType;
    this.viewParameter = viewParameter || this.viewParameter;
    this.sampleRate = sampleRate || this.sampleRate;
    const pageLoad = triggeredBy === 'pageLoad';
    const pageView = triggeredBy !== 'component'; // triggered by pageLoad or clientRouter
    this.cause = pageLoad
      ? PCONST.thrift.UpwtMetricCause.WEB_PAGE_LOAD
      : PCONST.thrift.UpwtMetricCause.USER_NAVIGATION;
    this.experimentMap = (experimentNames || []).reduce((map, name) => {
      const group = experiments.getInstance().v2GetGroup(name);
      return group ? { ...map, [name]: group } : map;
    }, {});

    const constraintAnnotations = [
      ...(endTimeConstraints || []),
      ...(measureMasonry ? ['MasonryDidMount'] : []),
    ];

    const allAnnotations = [
      ...(annotations || []),
      ...(measureMasonry ? ['MasonryConstructor'] : []),
      ...constraintAnnotations,
      ...(pageView
        ? ['GridBottomSpinnerStartedTraceEvent', 'GridBottomSpinnerStoppedTraceEvent']
        : []),
    ];

    const allBinaryAnnotations = [...(binaryAnnotations || []), 'NewAppVersionDetected'];

    const reportResult = (result: number, options?: { reason?: string, endTime?: number }) => {
      const timer = this._currTimer;
      if (!timer) {
        return;
      }
      const { reason, endTime } = options || {};
      timer.setResult(result, reason);
      timer.stopTiming(endTime);
      this.constructPWT();
    };

    const abortTimer = (reason?: string) =>
      reportResult(PCONST.thrift.TracingClientTypes.PWTResult.ABORTED, { reason });

    const constraintsDidEnd = (lastSubspanEnd: number | 'timeout') => {
      const timer = this._currTimer;
      if (!timer) {
        return;
      }
      if (lastSubspanEnd === 'timeout') {
        timer.addBinaryAnnotation({
          name: 'clientTimeout',
          value: true,
          annotation_type: 'BOOL',
        });
        reportResult(PCONST.thrift.TracingClientTypes.PWTResult.ERROR, { reason: 'clientTimeout' });
        return;
      }
      const lastAnnotationTime = timer.getLastAnnotationTime(constraintAnnotations);
      const maxEndTime = Math.max(lastSubspanEnd, lastAnnotationTime);
      if (maxEndTime) {
        if (lastSubspanEnd) {
          timer.addAnnotation('lastSubspanEnd', lastSubspanEnd);
        }
        timer.addBinaryAnnotation({
          name: 'lastEventIsSubspan',
          value: lastSubspanEnd >= lastAnnotationTime,
          annotation_type: 'BOOL',
        });
        reportResult(PCONST.thrift.TracingClientTypes.PWTResult.COMPLETE, {
          endTime: maxEndTime,
        });
      } else {
        // If we couldn't get an endtime, mark this timing as an error
        reportResult(PCONST.thrift.TracingClientTypes.PWTResult.ERROR);
      }
    };

    const gridHandler =
      measureMasonry &&
      getGridHandler({
        timerExists: () => !!this._currTimer,
        pageLoad,
        constraintsDidEnd,
      });

    // apply hooks
    const customEventHandlers = (customEventHooks || [])
      .map((hook) => hook())
      .concat(gridHandler ? [gridHandler.dispatch] : []);

    let routeChanged = false;

    this.cleanupCallbacks = [
      () => {
        routeChanged = false;
      },
      ...(gridHandler ? [gridHandler.cleanup] : []),
    ];

    const handleEvent = (event: TraceEvent): boolean => {
      const timer = this._currTimer;
      switch (event.name) {
        // start event starts timing and is the first event handled by the handler
        case 'start': {
          if (event.metricName !== metricName) {
            return false;
          }
          const timer = new Stopwatch({
            name: event.name,
            metricName,
            startTime: pageLoad ? window.performance.timing.navigationStart : Date.now(),
            spanId: (pageLoad && event.spanId) || null,
          });
          addStopwatchToDebugPerfLogger(timer);
          this._currTimer = timer;
          // handle case where None comes from python
          this._currTraceId =
            pageLoad && event.traceId && event.traceId !== 'None' ? event.traceId : randomTraceId();

          if (pageLoad) {
            // add annotations for events occurred before the start event is logged
            getAppInitAnnotations().forEach(({ timestamp, label }) =>
              timer.addAnnotation(label, timestamp),
            );
          }

          if (pageLoad) {
            if (DEBUG_ENABLED) {
              // Instead of marking the start time, indicate that the start time should be when the `navigationStart`
              // timestamp is so we can measure page loads later using the Performance API
              // TODO(jchan): Page loads are a special case because the stop timer happens when the images are
              // loaded and the page is interactive, which may not have a mark. So visualizations will be a little
              // misleading (stop event is going to be earlier than actual stop event). Figure out how to fix this.
              this.perfMarkStartName = 'navigationStart';
            }
          } else {
            if (DEBUG_TRACING) {
              // Mark the start time so we can measure it later using the Performance API
              this.perfMarkStartName = `${timer.uniqueName}_${timer.spanId}-start`;
              window.performance.mark(this.perfMarkStartName);
            }

            if (this.sampleRate !== 1) {
              this.state.isSampled = Math.random() < this.sampleRate;
            }
          }
          return true;
        }

        // stop event completes timing, serializes PWT data and reports to the server
        case 'stop': {
          if (!timer || event.metricName !== metricName) {
            return false;
          }

          if (pageLoad) {
            if (routeChanged) {
              // If we have navigated away from the page before the stop event is called, abort this timing
              abortTimer('routechange');
              return true;
            }
            addPageAnnotations(timer);
            addPageBinaryAnnotations(timer);
            addPageSubStopwatches(timer, metricName);
          } else {
            this.viewParameter = event.viewParameter || this.viewParameter;
            if (event.result === PCONST.thrift.TracingClientTypes.PWTResult.ABORTED) {
              abortTimer(event.resultReason);
              return true;
            }
          }

          const ignoreAllConstraints = Boolean(!pageLoad && event.gridImagesCached); // see https://phabricator.pinadmin.com/D272926
          if (!ignoreAllConstraints && (gridHandler || constraintAnnotations.length > 0)) {
            if (gridHandler) {
              if (inNewTraceLoggerExperiment()) {
                gridHandler.dispatch(event, timer);
              } else {
                timer.addConstraints(constraintAnnotations.concat(['image']));
                timer.stopTimingConstrainedMax().then(() => this.constructPWT());
              }
            } else {
              constraintsDidEnd(0);
            }
          } else {
            reportResult(event.result);
          }
          return true;
        }

        // records a route change event and this trace will result in abort
        case 'PageReactRouterOnChangeTraceEvent': {
          if (!timer) {
            return false;
          }
          if (pageLoad) {
            routeChanged = true;
            return true;
          }
          // exactly one route change is triggered. If any others occur after this, we'll abort the timing
          if (!routeChanged) {
            routeChanged = true;
          } else {
            abortTimer('routechange');
          }
          return true;
        }
        // abort timing
        case 'PageAbortTraceEvent': {
          if (!timer) {
            return false;
          }
          abortTimer(event.eventName);
          return true;
        }

        // the event does not match any of the core events
        default: {
          if (!timer) {
            return false;
          }
          const customTraceEvent: CustomTraceEvent = event; // the event must be a custom event
          const { name } = customTraceEvent;

          let handled = false;
          if (allAnnotations.includes(name)) {
            timer.addAnnotation(name);
            handled = true;
          }
          if (allBinaryAnnotations.includes(name)) {
            // Simply adds a binary annotation as to whether this trace event was received
            timer.addBinaryAnnotation({
              name,
              value: true,
              annotation_type: 'BOOL',
            });
            handled = true;
          }
          customEventHandlers.forEach((handleEvent) => {
            if (handleEvent(customTraceEvent, timer)) {
              handled = true;
            }
          });
          return handled;
        }
      }
    };

    this.routeEvent = (traceEvent: TraceEvent): boolean => {
      // If this event handler has a register for this event type, execute the
      // handler, but otherwise this should be a no-op.
      const handled = handleEvent(traceEvent);
      const timer = this._currTimer;
      // Log this event in the root subtimer for testing
      if (handled && timer) {
        timer.events.push(traceEvent);
        if (DEBUG_TRACING && this.perfMarkStartName) {
          // If an active timer is going, use performance timing API to visualize the measurement
          window.performance.mark(traceEvent.name);
          window.performance.measure(
            `\u23F0${traceEvent.name}`,
            this.perfMarkStartName,
            traceEvent.name,
          );
        }
      }
      return handled;
    };
  }
}
